/**
 * FarmyAnchoredDialog
 * Toggle-able dialog, will position and size itself relative to the first "position:relative" parent.
 * Its visibility is determined by the "show" prop.
 *
 * @param show (boolean => for custom logic)
 * @param position (up, down, left, right)
 * @param animate (boolean)
 * @param animationDirection (up, down, left, right)
 * @param closeOnClickOutside (boolean)
 */
import React, {useEffect, useState} from "react";
import useClickOutside from "../../shared/useClickOutside";
import currentTheme from "../../../react-themes/theme";

const theme = currentTheme();

const dirToTranslate = {
  up: "0px, 5px",
  down: "0px, -5px",
  left: "-5px, 0px",
  right: "5px, 0px"
};

const FarmyAnchoredDialog = (props) => {
  const {
    show,
    onClose = () => {},
    children,
    className = "",
    style,
    position = "top",
    animate = true,
    animationDirection = "up",
    closeOnClickOutside = true
  } = props;

  const [showDialog, setShow] = useState(show);
  const [visible, setVisible] = useState(!animate);
  const [toggling, setToggling] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);
  const timeout = theme.transitionTime.quiteFast;

  const positionStyles = {
    top: {bottom: "calc(100% + 5px)"},
    bottom: {top: "calc(100% + 5px)"}
  };

  const toggle = () => {
    setToggling(true);

    if (animate) {
      if (showDialog) {
        setVisible(false);

        const tId = setTimeout(() => {
          setShow(false);
          setToggling(false);
          onClose();
        }, timeout);

        setTimeoutId(tId);
      } else {
        setShow(true);

        requestAnimationFrame(() => {
          setVisible(true);
          setToggling(false);
        });
      }
    } else {
      setShow(!show);
      if (!show) onClose();
    }
  };

  closeOnClickOutside && useClickOutside(
    ".anchored-dialog",
    () => showDialog && toggle(),
    [showDialog]
  );

  useEffect(() => {
    if (!show && !showDialog) return;

    !toggling && toggle();
  }, [show]);

  useEffect(() => {
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return (<>
    {showDialog &&
      <div
        className={`anchored-dialog position-absolute ${className} z-index-top-layer`}
        style={{
          opacity: !animate || visible ? 1 : 0,
          transform: !animate || visible ? "translate(0, 0)" : `translate(${dirToTranslate[animationDirection]})`,
          transition: animate ? `all ${timeout}ms` : null,
          ...positionStyles[position],
          ...style
        }}
      >
        {children}
      </div>
    }
  </>
  );
};

export default FarmyAnchoredDialog;
